import React, { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useCart } from './CartContext';
import { shopApi } from '../services/api';
import { C, fmt } from './ShopLayout';

export default function ShopCart() {
  const { settings } = useOutletContext();
  const { items, remove, update, subtotal } = useCart();
  const navigate = useNavigate();
  const [discountInput, setDiscountInput] = useState('');
  const [discount, setDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);

  const shipping = subtotal >= (settings.free_shipping || 1500) ? 0 : (settings.shipping_cost || 89);
  const discountAmount = discount?.discount_amount || 0;
  const total = Math.max(0, subtotal - discountAmount) + shipping;
  const toFreeShipping = (settings.free_shipping || 1500) - subtotal;

  async function applyDiscount() {
    if (!discountInput.trim()) return;
    setDiscountLoading(true); setDiscountError('');
    try {
      const result = await shopApi.discount({ code: discountInput, subtotal });
      setDiscount(result);
    } catch (err) {
      setDiscountError(typeof err === 'string' ? err : 'Neplatný kód');
      setDiscount(null);
    } finally { setDiscountLoading(false); }
  }

  if (items.length === 0) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🛒</div>
      <div style={{ fontSize: 18, color: C.muted, marginBottom: 20 }}>Košík je prázdný</div>
      <Link to="/shop" style={{ display: 'inline-block', padding: '10px 24px', background: C.primary, color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 500 }}>
        Pokračovat v nákupu
      </Link>
    </div>
  );

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 24px 60px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 28, color: C.text }}>Košík</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>
        {/* Položky */}
        <div>
          {toFreeShipping > 0 && (
            <div style={{ background: '#EAF3DE', color: '#3B6D11', padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 20 }}>
              🎁 Přidej za {fmt(toFreeShipping)} a máš dopravu zdarma!
            </div>
          )}

          {items.map(item => (
            <div key={`${item.id}-${item.variant}`} style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
              <div style={{ width: 72, height: 72, background: '#f7f7f7', borderRadius: 8, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.image_url ? <img src={item.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 32 }}>📦</span>}
              </div>
              <div style={{ flex: 1 }}>
                <Link to={`/shop/${item.slug}`} style={{ fontWeight: 500, fontSize: 14, color: C.text, textDecoration: 'none', display: 'block', marginBottom: 3 }}>{item.name}</Link>
                {item.variant && <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{item.variant}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => update(item.id, item.variant, item.qty - 1)} style={{ width: 28, height: 28, border: `1px solid ${C.border}`, background: 'white', borderRadius: 5, cursor: 'pointer', fontSize: 15 }}>−</button>
                  <span style={{ fontWeight: 500, minWidth: 20, textAlign: 'center', fontSize: 14 }}>{item.qty}</span>
                  <button onClick={() => update(item.id, item.variant, item.qty + 1)} style={{ width: 28, height: 28, border: `1px solid ${C.border}`, background: 'white', borderRadius: 5, cursor: 'pointer', fontSize: 15 }}>+</button>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: C.primary, marginBottom: 8 }}>{fmt(item.price * item.qty)}</div>
                <button onClick={() => remove(item.id, item.variant)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18 }}>×</button>
              </div>
            </div>
          ))}

          {/* Slevový kód */}
          <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
            <input
              value={discountInput}
              onChange={e => setDiscountInput(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && applyDiscount()}
              placeholder="Slevový kód"
              style={{ flex: 1, padding: '9px 14px', border: `1.5px solid ${discount ? C.teal : C.border}`, borderRadius: 8, fontSize: 14, outline: 'none' }}
              disabled={!!discount}
            />
            {discount
              ? <button onClick={() => { setDiscount(null); setDiscountInput(''); }} style={{ padding: '9px 16px', background: '#f0f0f0', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: C.muted }}>Zrušit</button>
              : <button onClick={applyDiscount} disabled={discountLoading || !discountInput.trim()} style={{ padding: '9px 18px', background: C.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {discountLoading ? '…' : 'Uplatnit'}
                </button>
            }
          </div>
          {discount && <div style={{ fontSize: 13, color: C.teal, marginTop: 6 }}>✓ Kód uplatněn — sleva {fmt(discountAmount)}</div>}
          {discountError && <div style={{ fontSize: 13, color: '#A32D2D', marginTop: 6 }}>{discountError}</div>}
        </div>

        {/* Souhrn */}
        <div style={{ background: '#fafafa', borderRadius: 12, border: `0.5px solid ${C.border}`, padding: 24, position: 'sticky', top: 84 }}>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 20 }}>Souhrn objednávky</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.muted, marginBottom: 8 }}>
            <span>Zboží</span><span>{fmt(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.teal, marginBottom: 8 }}>
              <span>Sleva</span><span>−{fmt(discountAmount)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.muted, marginBottom: 16 }}>
            <span>Doprava</span>
            <span style={{ color: shipping === 0 ? C.teal : C.text }}>{shipping === 0 ? 'zdarma' : fmt(shipping)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: C.primary, paddingTop: 14, borderTop: `1px solid ${C.border}`, marginBottom: 20 }}>
            <span>Celkem</span><span>{fmt(total)}</span>
          </div>

          <button onClick={() => navigate('/shop/objednat', { state: { discountCode: discount ? discountInput : '', discountAmount } })}
            style={{ width: '100%', padding: '12px 0', background: C.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
            Pokračovat k objednávce →
          </button>
          <Link to="/shop" style={{ display: 'block', textAlign: 'center', fontSize: 13, color: C.muted, marginTop: 12, textDecoration: 'none' }}>
            ← Pokračovat v nákupu
          </Link>
        </div>
      </div>
    </div>
  );
}
